<template>
  <div class="transactions-view">
    <div class="header">
      <div class="header-top">
        <h1>Transactions</h1>
        <div class="header-buttons">
          <button @click="refreshFromMonarch" :disabled="refreshing" class="btn-refresh">
            {{ refreshing ? 'Refreshing...' : 'Refresh from Monarch' }}
          </button>
          <button @click="runAutoMatch" :disabled="autoMatching" class="btn-auto-match">
            {{ autoMatching ? 'Matching...' : 'Auto-Match' }}
          </button>
        </div>
      </div>
      <div class="header-filters">
        <select v-model="dateMode" @change="onDateModeChange">
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 180 days</option>
          <option value="custom">Custom range</option>
        </select>
        <template v-if="dateMode === 'custom'">
          <input type="date" v-model="customStart" class="date-input" @change="onCustomDateChange" />
          <span class="date-sep">to</span>
          <input type="date" v-model="customEnd" class="date-input" @change="onCustomDateChange" />
        </template>
        <select v-model="statusFilter" @change="loadMatched">
          <option :value="null">All Statuses</option>
          <option value="pending">Pending Parse</option>
          <option value="itemized">Pending Review</option>
          <option value="confirmed">Confirmed</option>
          <option value="synced">Synced</option>
        </select>
        <select v-model="displayLimit" class="limit-select">
          <option :value="25">Show 25</option>
          <option :value="50">Show 50</option>
          <option :value="100">Show 100</option>
          <option :value="0">Show All</option>
        </select>
      </div>
    </div>

    <!-- Sub-tabs -->
    <div class="sub-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab-btn', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
        <span class="tab-count" v-if="tab.count !== null">({{ tab.count }})</span>
      </button>
    </div>

    <div v-if="summary && activeTab !== 'summary'" class="summary-bar">
      <span>{{ summary.matched }} matched</span>
      <span class="sep">|</span>
      <span>{{ summary.unmatched_monarch }} unmatched Monarch</span>
      <span class="sep">|</span>
      <span>{{ summary.unmatched_gmail }} unmatched Gmail</span>
      <span class="sep">|</span>
      <span>{{ summary.total_monarch }} Monarch total, {{ summary.total_gmail }} Gmail total</span>
    </div>

    <div v-if="loading" class="loading">Loading...</div>

    <!-- Summary tab -->
    <template v-else-if="activeTab === 'summary'">
      <div v-if="categorySummary.length === 0" class="empty-state">
        <p>No categorized items found.</p>
      </div>
      <div v-else class="summary-table-wrapper">
        <table class="summary-table">
          <thead>
            <tr>
              <th>Category</th>
              <th class="th-right">Items</th>
              <th class="th-right">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in categorySummary" :key="row.category">
              <td>{{ row.category }}</td>
              <td class="td-right">{{ row.count }}</td>
              <td class="td-right amount-positive">${{ row.total.toFixed(2) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>Total</strong></td>
              <td class="td-right"><strong>{{ summaryTotalCount }}</strong></td>
              <td class="td-right amount-positive"><strong>${{ summaryTotalAmount.toFixed(2) }}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>

    <!-- Table tabs (all / matched / unmatched) -->
    <template v-else>
      <div v-if="filteredRowsBase.length === 0" class="empty-state">
        <p>No transactions found. Sync Monarch transactions and Gmail to get started.</p>
      </div>

      <div v-else class="matched-table-wrapper">
        <div v-if="displayLimit > 0 && filteredRowsBase.length > displayLimit" class="display-limit-notice">
          Showing {{ filteredRows.length }} of {{ filteredRowsBase.length }} transactions
        </div>
        <table class="matched-table">
          <thead>
            <tr>
              <th colspan="6" class="th-group monarch-group">Monarch</th>
              <th colspan="7" class="th-group gmail-group">Receipts</th>
              <th class="th-group th-actions-group">Actions</th>
            </tr>
            <tr>
              <th>Date</th>
              <th>Merchant</th>
              <th>Amount</th>
              <th>Category</th>
              <th>CC Status</th>
              <th>Fetched</th>
              <th>Order #</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Items</th>
              <th>Status</th>
              <th>Synced</th>
              <th>From</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in filteredRows"
              :key="idx"
              :class="rowClass(row)"
            >
              <!-- Monarch columns -->
              <template v-if="row.monarch">
                <td>{{ formatDate(row.monarch.date) }}</td>
                <td>{{ row.monarch.merchant || '—' }}</td>
                <td :class="amountClass(row.monarch.amount)">
                  ${{ Math.abs(row.monarch.amount).toFixed(2) }}
                </td>
                <td :title="getCategoryTooltip(row)" :class="{ 'category-multiple': getMonarchCategory(row) === 'Multiple' }">
                  {{ getMonarchCategory(row) }}
                </td>
                <td>
                  <span :class="['cc-status', row.monarch.pending ? 'cc-pending' : 'cc-posted']">
                    {{ row.monarch.pending ? 'Pending' : 'Posted' }}
                  </span>
                </td>
                <td class="fetched-cell">
                  {{ formatSyncedAt(row.monarch.fetched_at) }}
                </td>
              </template>
              <template v-else>
                <td colspan="6" class="no-match-cell">No Monarch match</td>
              </template>

              <!-- Gmail columns -->
              <template v-if="row.gmail">
                <td>
                  <a class="gmail-link" @click.stop="goToTransaction(row.gmail.id)">
                    {{ row.gmail.order_number ? `#${row.gmail.order_number}` : '—' }}
                  </a>
                </td>
                <td>{{ formatDate(row.gmail.order_date || row.gmail.email_date) || '—' }}</td>
                <td :class="amountClass(row.gmail.order_total)">
                  {{ formatAmount(row.gmail.order_total) }}
                </td>
                <td class="items-cell" :title="formatItemsTooltip(row.gmail.items)">
                  {{ formatItemsSummary(row.gmail.items) }}
                </td>
                <td>
                  <span :class="['status-badge', `status-${row.gmail.processing_status}`]">
                    {{ formatStatus(row.gmail.processing_status) }}
                  </span>
                </td>
                <td class="synced-cell">
                  {{ formatSyncedAt(row.gmail.synced_to_monarch_at) }}
                </td>
                <td class="from-cell">
                  {{ row.gmail.owner_name || '—' }}
                </td>
              </template>
              <template v-else>
                <td colspan="7" class="no-match-cell">No Gmail match</td>
              </template>

              <!-- Actions -->
              <td class="actions-cell">
                <template v-if="row.match_type">
                  <span class="match-type-badge">{{ formatMatchType(row.match_type) }}</span>
                  <button
                    class="btn-unmatch"
                    @click="unmatch(row)"
                    title="Unmatch"
                  >&times;</button>
                </template>
                <template v-else>
                  <button
                    class="btn-match"
                    @click="openMatchModal(row, idx)"
                  >Match</button>
                </template>
                <button
                  v-if="row.gmail"
                  class="btn-view"
                  @click="goToTransaction(row.gmail.id)"
                  title="View details"
                >&rarr;</button>
                <button
                  v-if="row.gmail && row.gmail.processing_status !== 'dismissed'"
                  class="btn-dismiss-row"
                  @click="openDismissModal(row)"
                  title="Dismiss receipt"
                >&times;</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Dismiss Confirmation Modal -->
    <div v-if="dismissModal.visible" class="modal-overlay" @click.self="closeDismissModal">
      <div class="dismiss-modal">
        <div class="modal-header">
          <h3>Dismiss Receipt?</h3>
          <button @click="closeDismissModal" class="btn-close">&times;</button>
        </div>
        <div class="modal-body">
          <p v-if="dismissModal.row?.gmail">
            Are you sure you want to dismiss this receipt?
          </p>
          <div class="dismiss-details" v-if="dismissModal.row?.gmail">
            <div><strong>Order #{{ dismissModal.row.gmail.order_number || '?' }}</strong></div>
            <div>{{ formatDate(dismissModal.row.gmail.order_date) }} &mdash; {{ formatAmount(dismissModal.row.gmail.order_total) }}</div>
            <div class="dismiss-hint">Dismissed receipts won't appear in your main transaction list and won't be synced to Monarch.</div>
          </div>
          <div class="dismiss-actions">
            <button @click="closeDismissModal" class="btn-cancel">Cancel</button>
            <button @click="confirmDismiss" :disabled="dismissing" class="btn-confirm-dismiss">
              {{ dismissing ? 'Dismissing...' : 'Dismiss' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Match Modal -->
    <div v-if="matchModal.visible" class="modal-overlay" @click.self="closeMatchModal">
      <div class="match-modal">
        <div class="modal-header">
          <h3>Select a transaction to match</h3>
          <button @click="closeMatchModal" class="btn-close">&times;</button>
        </div>
        <div class="modal-body">
          <p class="modal-context" v-if="matchModal.sourceRow?.monarch">
            Matching Monarch transaction:
            <strong>{{ matchModal.sourceRow.monarch.merchant }}</strong>
            — ${{ Math.abs(matchModal.sourceRow.monarch.amount).toFixed(2) }}
            on {{ matchModal.sourceRow.monarch.date }}
          </p>
          <p class="modal-context" v-else-if="matchModal.sourceRow?.gmail">
            Matching Gmail order:
            <strong>#{{ matchModal.sourceRow.gmail.order_number }}</strong>
            — {{ formatAmount(matchModal.sourceRow.gmail.order_total) }}
            on {{ formatDate(matchModal.sourceRow.gmail.order_date) }}
          </p>

          <div v-if="loadingAllMonarch" class="loading-candidates">
            Loading all Monarch transactions...
          </div>

          <div v-else-if="matchCandidates.length === 0" class="empty-candidates">
            No unmatched candidates available.
          </div>

          <div v-else class="candidates-list">
            <p v-if="matchModal.side === 'gmail'" class="modal-hint">
              Showing all Monarch transactions. Close matches highlighted.
            </p>
            <div
              v-for="candidate in matchCandidates"
              :key="candidate._key"
              :class="[
                'candidate-row',
                { 'amount-highlight': candidate._amountMatch },
                { 'close-highlight': candidate._closeMatch && !candidate._amountMatch }
              ]"
              @click="confirmMatch(candidate)"
            >
              <template v-if="matchModal.side === 'monarch'">
                <!-- Showing unmatched Gmail transactions -->
                <span class="cand-date">{{ formatDate(candidate.order_date) }}</span>
                <span class="cand-desc">Order #{{ candidate.order_number || '?' }}</span>
                <span class="cand-amount">{{ formatAmount(candidate.order_total) }}</span>
                <span class="cand-extra">{{ candidate.items?.length || 0 }} items</span>
              </template>
              <template v-else>
                <!-- Showing ALL unmatched Monarch transactions with diff info -->
                <div class="cand-main">
                  <span class="cand-date">{{ candidate.date }}</span>
                  <span class="cand-desc">{{ candidate.merchant || '—' }}</span>
                  <span class="cand-amount">${{ Math.abs(candidate.amount).toFixed(2) }}</span>
                  <span :class="['cand-diff', candidate._diff > 0 ? 'diff-over' : 'diff-under']">
                    {{ candidate._diff >= 0 ? '+' : '' }}${{ candidate._diff.toFixed(2) }}
                  </span>
                </div>
                <div class="cand-details">
                  <span class="cand-category">{{ candidate.category || 'Uncategorized' }}</span>
                  <span class="cand-account">{{ candidate.account || '' }}</span>
                  <span v-if="candidate.originalDescription" class="cand-orig">
                    {{ candidate.originalDescription }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTransactionsStore } from '../store/transactions'
import { syncAPI } from '../services/api'

const router = useRouter()
const route = useRoute()
const store = useTransactionsStore()

const loading = ref(false)
const autoMatching = ref(false)
const refreshing = ref(false)
const dateMode = ref('90')
const customStart = ref('')
const customEnd = ref('')
const statusFilter = ref(null)
const displayLimit = ref(50)
const allRows = ref([])
const summary = ref(null)
const activeTab = ref('receipts')

const matchModal = ref({
  visible: false,
  sourceRow: null,
  side: null,
})

const dismissModal = ref({
  visible: false,
  row: null,
})
const dismissing = ref(false)

// All Monarch transactions for fuzzy matching (loaded on demand)
const allMonarchForMatching = ref([])
const loadingAllMonarch = ref(false)

// Separate $0 receipts from regular transactions
const regularRows = computed(() => {
  return allRows.value.filter(r => {
    // Keep rows that have no gmail, or gmail with non-zero total
    if (!r.gmail) return true
    const total = parseFloat(r.gmail.order_total) || 0
    return total > 0
  })
})

const zeroTotalRows = computed(() => {
  return allRows.value.filter(r => {
    if (!r.gmail) return false
    const total = parseFloat(r.gmail.order_total) || 0
    return total === 0
  })
})

// Rows that have receipts (Gmail data) - excludes Monarch-only rows
const receiptsOnlyRows = computed(() => {
  return regularRows.value.filter(r => r.gmail)
})

// Tab definitions with counts
const tabs = computed(() => [
  { key: 'receipts', label: 'Receipts', count: receiptsOnlyRows.value.length },
  { key: 'all', label: 'All', count: regularRows.value.length },
  { key: 'matched', label: 'Matched', count: regularRows.value.filter(r => r.match_type).length },
  { key: 'unmatched', label: 'Unmatched', count: regularRows.value.filter(r => !r.match_type).length },
  { key: 'summary', label: 'By Category', count: null },
  { key: 'zero', label: '$0 Receipts', count: zeroTotalRows.value.length },
])

// Filtered rows based on active tab (before limit applied)
const filteredRowsBase = computed(() => {
  if (activeTab.value === 'zero') {
    return zeroTotalRows.value
  }
  if (activeTab.value === 'receipts') {
    return receiptsOnlyRows.value
  }
  if (activeTab.value === 'matched') {
    return regularRows.value.filter(r => r.match_type)
  }
  if (activeTab.value === 'unmatched') {
    return regularRows.value.filter(r => !r.match_type)
  }
  return regularRows.value
})

// Apply display limit
const filteredRows = computed(() => {
  const rows = filteredRowsBase.value
  if (displayLimit.value > 0 && rows.length > displayLimit.value) {
    return rows.slice(0, displayLimit.value)
  }
  return rows
})

// Category summary computed from gmail item data (excludes $0 receipts)
const categorySummary = computed(() => {
  const catMap = new Map()
  for (const row of regularRows.value) {
    if (!row.gmail?.items) continue
    for (const item of row.gmail.items) {
      // Use category_name if set, otherwise fall back to suggested_category_name
      const catName = item.category_name || item.suggested_category_name || 'Uncategorized'
      if (!catMap.has(catName)) {
        catMap.set(catName, { category: catName, count: 0, total: 0 })
      }
      const entry = catMap.get(catName)
      entry.count += item.quantity || 1
      entry.total += (item.amount || 0) * (item.quantity || 1)
    }
  }
  return Array.from(catMap.values()).sort((a, b) => b.total - a.total)
})

const summaryTotalCount = computed(() => categorySummary.value.reduce((s, r) => s + r.count, 0))
const summaryTotalAmount = computed(() => categorySummary.value.reduce((s, r) => s + r.total, 0))

onMounted(() => {
  // Read status from query params (e.g. from dashboard links)
  if (route.query.status) {
    statusFilter.value = route.query.status
  }
  loadMatched()
})

const onDateModeChange = () => {
  // Clear cached Monarch data so it reloads with new date range
  allMonarchForMatching.value = []
  if (dateMode.value !== 'custom') {
    loadMatched()
  }
}

const onCustomDateChange = () => {
  // Clear cached Monarch data so it reloads with new date range
  allMonarchForMatching.value = []
  loadMatched()
}

const loadMatched = async () => {
  try {
    loading.value = true
    const params = {}

    if (dateMode.value === 'custom') {
      if (customStart.value) params.start_date = customStart.value
      if (customEnd.value) params.end_date = customEnd.value
    } else {
      params.days = Number(dateMode.value)
    }

    if (statusFilter.value) {
      params.status = statusFilter.value
    }

    await store.fetchMatchedTransactions(params)
    allRows.value = store.matchedRows
    summary.value = store.matchSummary
  } catch (error) {
    console.error('Error loading matched transactions:', error)
  } finally {
    loading.value = false
  }
}

const runAutoMatch = async () => {
  try {
    autoMatching.value = true
    const result = await store.autoMatchTransactions()
    await loadMatched()
    if (result.matched > 0) {
      alert(`Auto-matched ${result.matched} transactions`)
    } else {
      alert('No new matches found')
    }
  } catch (error) {
    console.error('Error auto-matching:', error)
    alert('Auto-match failed: ' + (error.response?.data?.error || error.message))
  } finally {
    autoMatching.value = false
  }
}

const refreshFromMonarch = async () => {
  try {
    refreshing.value = true
    const days = dateMode.value === 'custom' ? 180 : Number(dateMode.value)
    await syncAPI.syncMonarchTransactions({ days })
    // Clear cached Monarch data so it reloads fresh
    allMonarchForMatching.value = []
    await loadMatched()
  } catch (error) {
    console.error('Error refreshing from Monarch:', error)
    alert('Refresh failed: ' + (error.response?.data?.error || error.message))
  } finally {
    refreshing.value = false
  }
}

// Unmatched Gmail candidates for the modal (excludes $0 receipts)
const unmatchedGmail = computed(() => {
  return regularRows.value
    .filter(r => r.gmail && !r.match_type)
    .map(r => r.gmail)
})

const matchCandidates = computed(() => {
  if (!matchModal.value.sourceRow) return []

  if (matchModal.value.side === 'monarch') {
    const monarchAmount = Math.abs(matchModal.value.sourceRow.monarch.amount)
    return unmatchedGmail.value.map(g => ({
      ...g,
      _key: `gmail-${g.id}`,
      _amountMatch: g.order_total !== null && Math.abs(Math.abs(g.order_total) - monarchAmount) < 0.02,
    })).sort((a, b) => {
      if (a._amountMatch && !b._amountMatch) return -1
      if (!a._amountMatch && b._amountMatch) return 1
      return 0
    })
  } else {
    // For Gmail rows, use ALL Monarch transactions for matching
    const gmailAmount = Math.abs(matchModal.value.sourceRow.gmail.order_total || 0)
    const gmailDate = matchModal.value.sourceRow.gmail.order_date
      ? new Date(matchModal.value.sourceRow.gmail.order_date)
      : null

    // Get already-matched Monarch IDs to exclude
    const matchedMonarchIds = new Set(
      regularRows.value
        .filter(r => r.match_type && r.monarch)
        .map(r => r.monarch.id)
    )

    return allMonarchForMatching.value
      .filter(m => !matchedMonarchIds.has(m.id))
      .map(m => {
        const monarchAmount = Math.abs(m.amount || 0)
        const diff = monarchAmount - gmailAmount
        const diffPct = gmailAmount > 0 ? Math.abs(diff / gmailAmount) * 100 : 0

        // Calculate date difference
        let dateDiff = null
        if (gmailDate && m.date) {
          const mDate = new Date(m.date)
          dateDiff = Math.round((mDate - gmailDate) / (1000 * 60 * 60 * 24))
        }

        return {
          ...m,
          _key: `monarch-${m.id}`,
          _diff: diff,
          _diffPct: diffPct,
          _dateDiff: dateDiff,
          _amountMatch: Math.abs(diff) < 0.02,
          _closeMatch: Math.abs(diff) < 5 || diffPct < 10, // Within $5 or 10%
        }
      })
      .sort((a, b) => {
        // Sort by: exact match first, then close matches, then by absolute difference
        if (a._amountMatch && !b._amountMatch) return -1
        if (!a._amountMatch && b._amountMatch) return 1
        if (a._closeMatch && !b._closeMatch) return -1
        if (!a._closeMatch && b._closeMatch) return 1
        return Math.abs(a._diff) - Math.abs(b._diff)
      })
  }
})

const openMatchModal = async (row) => {
  if (row.monarch && !row.gmail) {
    matchModal.value = { visible: true, sourceRow: row, side: 'monarch' }
  } else if (row.gmail && !row.monarch) {
    matchModal.value = { visible: true, sourceRow: row, side: 'gmail' }
    // Fetch ALL Monarch transactions for fuzzy matching
    await loadAllMonarchTransactions()
  }
}

const loadAllMonarchTransactions = async () => {
  if (allMonarchForMatching.value.length > 0) return // Already loaded

  try {
    loadingAllMonarch.value = true
    // Fetch without merchant filter to get all transactions
    const days = dateMode.value === 'custom' ? 180 : Number(dateMode.value)
    const response = await syncAPI.getMonarchTransactions({ days })
    allMonarchForMatching.value = response.data.transactions || []
  } catch (error) {
    console.error('Error loading Monarch transactions for matching:', error)
  } finally {
    loadingAllMonarch.value = false
  }
}

const closeMatchModal = () => {
  matchModal.value = { visible: false, sourceRow: null, side: null }
}

const confirmMatch = async (candidate) => {
  try {
    if (matchModal.value.side === 'monarch') {
      await store.matchTransaction(candidate.id, matchModal.value.sourceRow.monarch.id)
    } else {
      await store.matchTransaction(matchModal.value.sourceRow.gmail.id, candidate.id)
    }
    closeMatchModal()
    await loadMatched()
  } catch (error) {
    alert('Match failed: ' + (error.response?.data?.error || error.message))
  }
}

const unmatch = async (row) => {
  if (!row.gmail) return
  try {
    await store.unmatchTransaction(row.gmail.id)
    await loadMatched()
  } catch (error) {
    alert('Unmatch failed: ' + (error.response?.data?.error || error.message))
  }
}

const openDismissModal = (row) => {
  dismissModal.value = { visible: true, row }
}

const closeDismissModal = () => {
  dismissModal.value = { visible: false, row: null }
}

const confirmDismiss = async () => {
  if (!dismissModal.value.row?.gmail) return
  try {
    dismissing.value = true
    await store.dismissTransaction(dismissModal.value.row.gmail.id)
    closeDismissModal()
    await loadMatched()
  } catch (error) {
    alert('Dismiss failed: ' + (error.response?.data?.error || error.message))
  } finally {
    dismissing.value = false
  }
}

const goToTransaction = (id) => {
  router.push(`/transactions/${id}`)
}

const rowClass = (row) => {
  if (row.match_type) return 'row-matched'
  if (row.monarch && !row.gmail) return 'row-monarch-only'
  if (!row.monarch && row.gmail) return 'row-gmail-only'
  return ''
}

const amountClass = (amount) => {
  if (amount === null || amount === undefined) return ''
  return amount < 0 ? 'amount-negative' : 'amount-positive'
}

const formatDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString()
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '$0.00'
  return `$${Number(amount).toFixed(2)}`
}

const formatStatus = (status) => {
  const labels = {
    pending: 'Pending',
    itemized: 'Needs Review',
    confirmed: 'Confirmed',
    synced: 'Synced',
    dismissed: 'Dismissed'
  }
  return labels[status] || status
}

const getMonarchCategory = (row) => {
  // If Gmail transaction is synced, show categories from items (the actual split)
  if (row.gmail?.processing_status === 'synced' && row.gmail?.items?.length > 0) {
    const categories = [...new Set(
      row.gmail.items
        .map(item => item.category_name)
        .filter(Boolean)
    )]
    if (categories.length === 0) {
      return row.monarch?.category || '—'
    }
    if (categories.length === 1) {
      return categories[0]
    }
    // Multiple categories - show "Multiple" with tooltip
    return 'Multiple'
  }
  // Fall back to Monarch's original category
  return row.monarch?.category || '—'
}

const getCategoryTooltip = (row) => {
  if (row.gmail?.processing_status === 'synced' && row.gmail?.items?.length > 0) {
    const categories = [...new Set(
      row.gmail.items
        .map(item => item.category_name)
        .filter(Boolean)
    )]
    if (categories.length > 1) {
      return categories.join(', ')
    }
  }
  return ''
}

const formatMatchType = (type) => {
  const labels = {
    auto_matched: 'Auto',
    manually_matched: 'Manual',
  }
  return labels[type] || type
}

const formatSyncedAt = (dateStr) => {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  // Show relative time for recent syncs, absolute for older ones
  const now = new Date()
  const diffMs = now - d
  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffHours < 24) {
    // Within last 24 hours - show time
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (diffHours < 24 * 7) {
    // Within last week - show day and time
    return d.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })
  }
  // Older - show date
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const formatItemsSummary = (items) => {
  if (!items || items.length === 0) return '—'
  const totalQty = items.reduce((sum, i) => sum + (i.quantity || 1), 0)
  return `${totalQty} item${totalQty !== 1 ? 's' : ''}`
}

const formatItemsTooltip = (items) => {
  if (!items || items.length === 0) return ''
  return items.map(i => {
    const qty = i.quantity || 1
    const amt = i.amount ? `$${Number(i.amount).toFixed(2)}` : ''
    const name = i.name || 'Unknown item'
    return qty > 1 ? `${qty}x ${name} (${amt} ea)` : `${name} (${amt})`
  }).join('\n')
}
</script>

<style scoped>
.transactions-view {
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  margin-bottom: 1rem;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

h1 { color: #2c3e50; margin: 0; }

.header-buttons {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.header-filters select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.limit-select {
  margin-left: auto;
}

.date-input {
  padding: 0.45rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #2c3e50;
}
.date-input:focus {
  outline: none;
  border-color: #3498db;
}

.date-sep {
  color: #7f8c8d;
  font-size: 0.85rem;
}

/* Sub-tabs */
.sub-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
  border-bottom: 2px solid #ecf0f1;
}

.tab-btn {
  padding: 0.6rem 1.25rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #7f8c8d;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #2c3e50;
}

.tab-btn.active {
  color: #3498db;
  border-bottom-color: #3498db;
  font-weight: 600;
}

.tab-count {
  font-weight: 400;
  font-size: 0.8rem;
  opacity: 0.7;
}

.btn-refresh {
  padding: 0.5rem 1rem;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}
.btn-refresh:hover:not(:disabled) { background: #229954; }
.btn-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-auto-match {
  padding: 0.5rem 1rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}
.btn-auto-match:hover:not(:disabled) { background: #2980b9; }
.btn-auto-match:disabled { opacity: 0.6; cursor: not-allowed; }

.summary-bar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.sep { color: #bdc3c7; }

.loading, .empty-state {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}

.display-limit-notice {
  font-size: 0.85rem;
  color: #7f8c8d;
  margin-bottom: 0.5rem;
  font-style: italic;
}

/* Summary table */
.summary-table-wrapper {
  overflow-x: auto;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
}

.summary-table th {
  background: #f8f9fa;
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ecf0f1;
}

.summary-table td {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #ecf0f1;
  color: #2c3e50;
}

.summary-table tbody tr:hover {
  background-color: #f8f9fa;
}

.th-right { text-align: right; }
.td-right { text-align: right; }

.total-row {
  background-color: #f8f9fa;
}
.total-row td {
  border-top: 2px solid #ecf0f1;
}

/* Matched table */
.matched-table-wrapper {
  overflow-x: auto;
}

.matched-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 0.85rem;
}

.matched-table th {
  background: #f8f9fa;
  text-align: left;
  padding: 0.6rem 0.75rem;
  font-size: 0.8rem;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ecf0f1;
  white-space: nowrap;
}

.th-group {
  text-align: center !important;
  font-size: 0.75rem !important;
  padding: 0.4rem 0.75rem !important;
  border-bottom: 1px solid #ecf0f1 !important;
}
.monarch-group { background: #eef6ff !important; color: #2c5282 !important; }
.gmail-group { background: #fef5e7 !important; color: #8a6d3b !important; }
.th-actions-group { background: #f8f9fa !important; color: #7f8c8d !important; width: 120px; }

.matched-table td {
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #ecf0f1;
  color: #2c3e50;
  vertical-align: middle;
}

.matched-table tbody tr:hover {
  background-color: #f8f9fa;
}

.row-matched {
  background-color: #f0faf0;
}
.row-matched:hover {
  background-color: #e6f7e6 !important;
}

.row-monarch-only td {
  background-color: #fafcff;
}

.row-gmail-only td {
  background-color: #fffcf5;
}

.no-match-cell {
  color: #bdc3c7;
  font-style: italic;
  text-align: center;
}

.amount-negative { color: #e74c3c; font-weight: 600; }
.amount-positive { color: #27ae60; font-weight: 600; }

.gmail-link {
  color: #3498db;
  cursor: pointer;
  text-decoration: underline;
}
.gmail-link:hover { color: #2980b9; }

.status-badge {
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  text-transform: capitalize;
  white-space: nowrap;
}
.status-pending { background-color: #ecf0f1; color: #7f8c8d; }
.status-itemized { background-color: #fff3cd; color: #856404; }
.status-confirmed { background-color: #d4edda; color: #155724; }
.status-synced { background-color: #d1ecf1; color: #0c5460; }
.status-dismissed { background-color: #e0e0e0; color: #7f8c8d; }

.cc-status {
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  white-space: nowrap;
}
.cc-pending { background-color: #fff3cd; color: #856404; }
.cc-posted { background-color: #d4edda; color: #155724; }

.category-multiple {
  color: #8e44ad;
  font-style: italic;
  cursor: help;
}

.synced-cell, .fetched-cell {
  font-size: 0.8rem;
  color: #7f8c8d;
  white-space: nowrap;
}

.items-cell {
  font-size: 0.8rem;
  color: #7f8c8d;
  cursor: help;
  white-space: nowrap;
}

.actions-cell {
  white-space: nowrap;
}

.match-type-badge {
  font-size: 0.7rem;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  background: #d4edda;
  color: #155724;
  font-weight: 600;
  text-transform: uppercase;
}

.btn-match {
  padding: 0.25rem 0.6rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.75rem;
}
.btn-match:hover { background: #2980b9; }

.btn-unmatch {
  padding: 0.1rem 0.4rem;
  background: #f8d7da;
  color: #721c24;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.85rem;
  line-height: 1;
  margin-left: 0.3rem;
}
.btn-unmatch:hover { background: #f1b0b7; }

.btn-view {
  padding: 0.25rem 0.5rem;
  background: #ecf0f1;
  color: #2c3e50;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-left: 0.3rem;
}
.btn-view:hover { background: #d5dbdb; }

/* Match Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.match-modal {
  background: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #ecf0f1;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  line-height: 1;
}

.modal-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
}

.modal-context {
  font-size: 0.9rem;
  color: #7f8c8d;
  margin: 0 0 1rem 0;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.empty-candidates {
  text-align: center;
  padding: 2rem;
  color: #7f8c8d;
}

.candidates-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.candidate-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid #ecf0f1;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.candidate-row:hover {
  background: #f0f7ff;
  border-color: #3498db;
}

.candidate-row.amount-highlight {
  border-color: #27ae60;
  background: #f0faf0;
}
.candidate-row.amount-highlight:hover {
  background: #e6f7e6;
}

.cand-date { font-size: 0.85rem; color: #7f8c8d; min-width: 80px; }
.cand-desc { flex: 1; font-weight: 500; color: #2c3e50; font-size: 0.85rem; }
.cand-amount { font-weight: 600; color: #27ae60; font-size: 0.85rem; }
.cand-extra { font-size: 0.8rem; color: #7f8c8d; }

/* Enhanced match modal styles */
.loading-candidates {
  text-align: center;
  padding: 2rem;
  color: #7f8c8d;
}

.modal-hint {
  font-size: 0.8rem;
  color: #7f8c8d;
  margin: 0 0 0.75rem 0;
  font-style: italic;
}

.candidate-row.close-highlight {
  border-color: #f39c12;
  background: #fef9e7;
}
.candidate-row.close-highlight:hover {
  background: #fdf3d1;
}

.candidate-row {
  flex-wrap: wrap;
}

.cand-main {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.cand-details {
  display: flex;
  gap: 1rem;
  width: 100%;
  font-size: 0.75rem;
  color: #95a5a6;
  margin-top: 0.25rem;
  padding-left: 90px;
}

.cand-diff {
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 60px;
  text-align: right;
}
.diff-over { color: #e74c3c; }
.diff-under { color: #27ae60; }

.cand-category {
  background: #ecf0f1;
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
}

.cand-account {
  color: #7f8c8d;
}

.cand-orig {
  color: #bdc3c7;
  font-style: italic;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Dismiss button */
.btn-dismiss-row {
  padding: 0.1rem 0.4rem;
  background: none;
  color: #e74c3c;
  border: 1px solid #e74c3c;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.85rem;
  line-height: 1;
  margin-left: 0.3rem;
}
.btn-dismiss-row:hover {
  background: #e74c3c;
  color: white;
}

/* Dismiss Modal */
.dismiss-modal {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.dismiss-modal .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #ecf0f1;
}

.dismiss-modal .modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1rem;
}

.dismiss-modal .modal-body {
  padding: 1.25rem;
}

.dismiss-modal .modal-body p {
  margin: 0 0 1rem 0;
  color: #7f8c8d;
}

.dismiss-details {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #2c3e50;
}

.dismiss-hint {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #7f8c8d;
}

.dismiss-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: #ecf0f1;
  color: #7f8c8d;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-cancel:hover {
  background: #bdc3c7;
  color: #2c3e50;
}

.btn-confirm-dismiss {
  padding: 0.5rem 1rem;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-confirm-dismiss:hover:not(:disabled) {
  background: #c0392b;
}
.btn-confirm-dismiss:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
